import type { Actor } from '@ofmos/shared/actor';
import { formatDateTime, formatRelative } from '../../fields/format';
import { Avatar, cx, Icon } from '../../ui';

/**
 * WHO WROTE THIS ANNOTATION, and whether a machine wrote it
 * (notes-tasks-comments.md 12.3).
 *
 * INV-3's product promise is that an AI write is distinguishable at a glance. That
 * promise is kept HERE, in one component, rather than by every screen remembering to
 * render a badge: a note card, a comment and a timeline entry all go through this, so a
 * new surface inherits the distinction instead of having to think of it.
 *
 * The actor here is the SHARED one, not the web client's display-augmented variant: an
 * annotation summary comes straight from `@ofmos/shared/annotations`, and re-mapping it
 * into a second Actor shape would buy nothing but a place for the two to disagree.
 *
 * A HUMAN-ADOPTED agent note (2.5 rule 3) reads "AI assistant, edited by a person" rather than
 * dropping the badge outright. The machine still wrote the words; what changed is that
 * a human has taken responsibility for them, and both facts matter to somebody auditing
 * the record.
 */
export interface AnnotationAuthorProps {
  readonly createdBy: Actor;
  readonly createdByName: string | null;
  readonly updatedByName?: string | null | undefined;
  readonly at: string;
  /** Set once a person edited it, which adopts it. */
  readonly humanTouchedAt?: string | null | undefined;
  /** Shown after the timestamp when the body has been changed since it was written. */
  readonly edited?: boolean | undefined;
  readonly className?: string | undefined;
}

export function AnnotationAuthor({
  createdBy,
  createdByName,
  updatedByName,
  at,
  humanTouchedAt,
  edited,
  className,
}: AnnotationAuthorProps) {
  const machine = createdBy.source !== 'MANUAL';
  // DESIGN.md D5 (register A5-5): the product agent is "AI assistant"; session/key labels are internal.
  const name =
    createdBy.source === 'AGENT'
      ? 'AI assistant'
      : (createdByName ?? createdBy.context ?? sourceLabel(createdBy.source));
  const adopted = machine && (humanTouchedAt ?? null) !== null;

  return (
    <div className={cx('flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      {machine ? (
        /* The symbol, unboxed, for the reason `ActorLabel` gives: the kit's leading Symbol
           accessory is a glyph in the label's colour and never a framed square. */
        <span className="inline-flex shrink-0 items-center justify-center text-accent">
          <Icon name={createdBy.source === 'AGENT' ? 'agent' : 'link'} size="0.9rem" />
        </span>
      ) : (
        <Avatar name={name} size="sm" />
      )}

      <span className="truncate text-sm font-medium text-ink">{name}</span>

      {createdBy.source !== 'AGENT' && <SourceBadge createdBy={createdBy} />}
      {adopted && (
        <span className="text-2xs text-ink-subtle">edited by {updatedByName ?? 'a person'}</span>
      )}

      <time
        dateTime={at}
        title={formatDateTime(at)}
        className="text-2xs text-ink-subtle tabular-nums"
      >
        {formatRelative(at)}
      </time>
      {edited === true && !adopted && <span className="text-2xs text-ink-subtle">edited</span>}
    </div>
  );
}

/**
 * WHO WROTE IT, IN ONE BADGE - extracted so a DENSE row can carry INV-3's promise without
 * carrying the whole author block (dashboard.md D28).
 *
 * The dashboard's pooled list is a 44px row with a checkbox, a title, a parent line and a
 * due badge; an avatar, a name and a relative timestamp would not fit and would not be
 * what the row is for. What must survive into it is the one thing INV-3 promises - an AI
 * write is distinguishable at a glance - so the badge moved into its own component and
 * `AnnotationAuthor` renders it too. One implementation, two densities, and no screen
 * remembering to add a badge.
 *
 * A MANUAL write renders NOTHING. Every task in this database was typed by a person
 * today, so a badge on all of them would be noise that trains people to ignore it.
 */
export function SourceBadge({ createdBy }: { readonly createdBy: Actor }) {
  if (createdBy.source === 'MANUAL') return null;
  /*
    WAVE 3 (R-B1): THE WORDS, IN THE ACCENT, WITH NO CAPSULE UNDER THEM.
    A tinted capsule is the product's TAG shape and a writer's kind is not a tag - the
    owner's round-2 decision took `Badge` off every read surface of the record, and this was
    the last one on it. Two words in the accent role say the same thing, and `ActorLabel`
    beside it in the same stream already says its kind exactly this way, so the timeline and
    the note cards stop describing one fact in two shapes. Home's pooled task row reads this
    component too and loses the capsule with it, which is the point of it being one component.
    hig: https://developer.apple.com/design/human-interface-guidelines/color
  */
  return (
    <span className="text-2xs font-medium text-accent">
      {createdBy.source === 'AGENT' ? 'AI assistant' : sourceLabel(createdBy.source)}
    </span>
  );
}

function sourceLabel(source: Actor['source']): string {
  switch (source) {
    case 'AGENT':
      return 'AI assistant';
    case 'IMPORT':
      return 'Import';
    case 'WORKFLOW':
      return 'Workflow';
    case 'API':
      return 'API';
    case 'SYSTEM':
      return 'System';
    default:
      return 'Unknown';
  }
}
